// receiptPrinter.ts
// Epson ePOS SDK (epos-2.27.0.js) must be loaded via <script> in public/index.html
// Example: <script src="%PUBLIC_URL%/epos-2.27.0.js"></script>

export type ReceiptPayload = {
    receiptId: string;
    amountDue: number;
    cashReceived: number;
    changeDue: number;
    paidAtIso: string;
  
    // Optional extras you’ll likely add later
    merchantName?: string;
    merchantAddressLines?: string[];
    lineItems?: Array<{ name: string; qty?: number; price: number }>;
    footerLines?: string[];
  };
  
  export type EpsonConnectionConfig = {
    host: string;          // printer IP or hostname on LAN
    port?: number;         // often 8008 for ePOS
    deviceId?: string;     // depends on printer/config; many samples use "local_printer"
    timeoutMs?: number;    // connect timeout
    crypto?: boolean;      // ePOS option
    buffer?: boolean;      // ePOS option
  };
  
  type EpsonGlobal = {
    ePOSDevice: new () => EpsonEposDevice;
  };
  
  type EpsonEposDevice = {
    // constants often present on instance
    DEVICE_TYPE_PRINTER: string | number;
  
    connect: (
      host: string,
      port: number,
      callback: (result: string) => void,
      options?: any
    ) => void;
  
    disconnect: () => void;
  
    createDevice: (
      deviceId: string,
      deviceType: any,
      options: any,
      callback: (deviceObj: EpsonPrinterDevice | null, result: string) => void
    ) => void;
  };
  
  type EpsonPrinterDevice = {
    // Common constants on printer device
    ALIGN_LEFT: number;
    ALIGN_CENTER: number;
    ALIGN_RIGHT: number;
  
    CUT_FEED: number;
    CUT_NO_FEED: number;
  
    // Builder methods
    addTextAlign: (align: number) => void;
    addText: (text: string) => void;
    addFeedLine: (lines: number) => void;
    addCut: (cutType: number) => void;
  
    // Optional formatting methods (vary by printer/SDK)
    addTextSize?: (w: number, h: number) => void;
    addTextStyle?: (reverse: boolean, ul: boolean, em: boolean, color: number) => void;
    addPulse?: (drawer: number, time: number) => void;
  
    // Send
    send: (callback: (result: string) => void) => void;
  
    // Event handlers (optional)
    onreceive?: (res: { success: boolean; code: string; status?: number }) => void;
  };
  
  function formatMoney(n: number): string {
    // keep simple (you can swap to Intl.NumberFormat later)
    return `$${n.toFixed(2)}`;
  }
  
  function safeParseDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  }
  
  function getEpsonGlobal(): EpsonGlobal | null {
    const w = window as any;
    // Epson attaches under window.epson in this SDK
    return w?.epson?.ePOSDevice ? (w.epson as EpsonGlobal) : null;
  }
  
  /**
   * A small singleton-ish manager so you can call printReceipt() anywhere.
   * It will connect on demand and reuse the same connection.
   */
  class EpsonReceiptPrinter {
    private eposDevice: EpsonEposDevice | null = null;
    private printer: EpsonPrinterDevice | null = null;
    private connecting: Promise<void> | null = null;
    private connectedConfigKey: string | null = null;
  
    private makeConfigKey(cfg: EpsonConnectionConfig) {
      const port = cfg.port ?? 8008;
      const deviceId = cfg.deviceId ?? "local_printer";
      return `${cfg.host}:${port}:${deviceId}`;
    }
  
    async connect(cfg: EpsonConnectionConfig): Promise<void> {
      const epson = getEpsonGlobal();
      if (!epson) {
        throw new Error(
          "Epson ePOS SDK not found. Ensure epos-2.27.0.js is loaded via a <script> tag."
        );
      }
  
      const key = this.makeConfigKey(cfg);
      if (this.printer && this.eposDevice && this.connectedConfigKey === key) return;
  
      // Avoid parallel connect attempts
      if (this.connecting) return this.connecting;
  
      this.connecting = (async () => {
        // If config changed, reset first
        if (this.eposDevice) {
          try {
            this.eposDevice.disconnect();
          } catch {
            // ignore
          }
          this.eposDevice = null;
          this.printer = null;
          this.connectedConfigKey = null;
        }
  
        const port = cfg.port ?? 8008;
        const deviceId = cfg.deviceId ?? "local_printer";
        const timeoutMs = cfg.timeoutMs ?? 8000;
  
        const eposDevice = new epson.ePOSDevice();
        this.eposDevice = eposDevice;
  
        // connect
        await withTimeout(
          new Promise<void>((resolve, reject) => {
            eposDevice.connect(cfg.host, port, (result: string) => {
              if (result === "OK") resolve();
              else reject(new Error(`ePOSDevice.connect failed: ${result}`));
            });
          }),
          timeoutMs,
          `Timed out connecting to printer at ${cfg.host}:${port}`
        );
  
        // create device
        await withTimeout(
          new Promise<void>((resolve, reject) => {
            const deviceType = (eposDevice as any).DEVICE_TYPE_PRINTER ?? "printer";
            eposDevice.createDevice(
              deviceId,
              deviceType,
              {
                crypto: cfg.crypto ?? false,
                buffer: cfg.buffer ?? false,
              },
              (deviceObj: EpsonPrinterDevice | null, result: string) => {
                if (result === "OK" && deviceObj) {
                  this.printer = deviceObj;
                  this.connectedConfigKey = key;
                  resolve();
                } else {
                  reject(new Error(`createDevice failed: ${result}`));
                }
              }
            );
          }),
          timeoutMs,
          `Timed out creating printer device (${deviceId})`
        );
      })().finally(() => {
        this.connecting = null;
      });
  
      return this.connecting;
    }
  
    disconnect(): void {
      try {
        this.eposDevice?.disconnect();
      } finally {
        this.eposDevice = null;
        this.printer = null;
        this.connectedConfigKey = null;
        this.connecting = null;
      }
    }
  
    async print(payload: ReceiptPayload, cfg: EpsonConnectionConfig): Promise<void> {
      await this.connect(cfg);
  
      const printer = this.printer;
      if (!printer) throw new Error("Printer not initialized after connect().");
  
      // Optional: prefer event-style receive callback if available
      // We'll also resolve based on send callback as a baseline.
      const sendPromise = new Promise<void>((resolve, reject) => {
        printer.send((result: string) => {
          if (result === "OK") resolve();
          else reject(new Error(`printer.send failed: ${result}`));
        });
      });
  
      // Build receipt contents
      buildReceipt(printer, payload);
  
      // send
      await sendPromise;
    }
  }
  
  function buildReceipt(printer: EpsonPrinterDevice, payload: ReceiptPayload) {
    const merchant = payload.merchantName ?? "Receipt";
    const when = safeParseDate(payload.paidAtIso);
  
    // Header
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addText(`${merchant}\n`);
    if (payload.merchantAddressLines?.length) {
      for (const line of payload.merchantAddressLines) printer.addText(`${line}\n`);
    }
    printer.addText(`\nPAID: ${when}\n`);
    printer.addText(`Receipt: ${payload.receiptId}\n`);
    printer.addFeedLine(1);
  
    // Items (optional)
    printer.addTextAlign(printer.ALIGN_LEFT);
    if (payload.lineItems?.length) {
      printer.addText(`ITEMS\n`);
      printer.addText(`-----------------------------\n`);
      for (const item of payload.lineItems) {
        const qty = item.qty ?? 1;
        // Keep to simple monospaced-ish formatting; tweak widths later for your printer
        const name = `${qty}x ${item.name}`.slice(0, 18).padEnd(18, " ");
        const price = formatMoney(item.price).padStart(10, " ");
        printer.addText(`${name}${price}\n`);
      }
      printer.addText(`-----------------------------\n`);
    }
  
    // Totals
    printer.addText(`Amount Due:   ${formatMoney(payload.amountDue)}\n`);
    printer.addText(`Cash:         ${formatMoney(payload.cashReceived)}\n`);
    printer.addText(`Change:       ${formatMoney(payload.changeDue)}\n`);
  
    printer.addFeedLine(1);
  
    // Footer
    printer.addTextAlign(printer.ALIGN_CENTER);
    if (payload.footerLines?.length) {
      for (const line of payload.footerLines) printer.addText(`${line}\n`);
    } else {
      printer.addText("Thank you!\n");
    }
  
    printer.addFeedLine(2);
    printer.addCut(printer.CUT_FEED);
  }
  
  function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
    let t: number | undefined;
    const timeout = new Promise<T>((_, reject) => {
      t = window.setTimeout(() => reject(new Error(msg)), ms);
    });
    return Promise.race([p, timeout]).finally(() => {
      if (t !== undefined) window.clearTimeout(t);
    });
  }
  
  // Singleton instance for app-wide use
  const EPSON = new EpsonReceiptPrinter();
  
  /**
   * Public API you can call from checkout flow.
   * You can choose your own config source (env vars, shop settings, user settings, etc.)
   */
  export async function printReceipt(
    payload: ReceiptPayload,
    opts?: {
      epson?: EpsonConnectionConfig;
      fallbackToBrowserPrint?: boolean;
    }
  ) {
    const fallbackToBrowserPrint = opts?.fallbackToBrowserPrint ?? false;
    const epsonCfg = opts?.epson;
  
    // If no Epson config provided, behave like your original stub
    if (!epsonCfg) {
      console.log("PRINT RECEIPT payload:", payload);
      if (fallbackToBrowserPrint) window.print();
      return;
    }
  
    try {
      await EPSON.print(payload, epsonCfg);
    } catch (err) {
      console.error("Epson print failed:", err);
  
      // Optional fallback
      if (fallbackToBrowserPrint) {
        // In production you probably render a hidden receipt component and print that.
        window.print();
        return;
      }
  
      // Bubble up so your UI can show "Printer offline" etc.
      throw err;
    }
  }
  
  /** If you need a “printer reset” button in settings UI */
  export function disconnectReceiptPrinter() {
    EPSON.disconnect();
  }